/**
 * Per-registry-key upload flows: read workbook → build list → call API.
 * Used only from `ChemUploadRegistry.js` so `ChemUploadExcelParseUtils` stays parsing/helpers.
 */

import { toast } from "react-toastify";
import {
  sendParsedValues,
  sendFapGenistaID,
  sendResidualSolventsGenistaID,
  updateChemistryTestResults,
  updateDynamicResults,
  updateMetalsResults,
  updateMycotoxinResults,
  updatePolyphenolResults,
  updatePunicalaginResults,
  updateResults,
  updateSilverNitrateResults,
  updateTitratableAcitidyResults,
} from "../../../actions/chemistry";
import { uploadFileToS3 } from "../../../utils/helpers";
import { s3AutomationPrivate } from "../../../utils/aws";
import { parsePesticide } from "./Helper";
import {
  buildAgilentMetalsUploadList,
  buildChemistryTestResultsList,
  buildDynamicOneToManyList,
  buildDynamicOneToOneList,
  buildFapAnalyticalResults,
  buildHeavyMetalsUploadList,
  buildLegacyDynamicExcelResultList,
  buildMoistureMultiTestList,
  buildMycotoxinUploadList,
  buildPolyphenolsResultList,
  buildPunicalaginsResultList,
  buildSilverNitrateResultList,
  buildTitratableAcidityUploadList,
  extractFapGenistaIdFromRows,
  extractResidualSolventsGenistaIdFromRows,
  FAP_ANALYTE_MAPPING,
  generateFapPdfFile,
  buildResidualSolventsTableFromRows,
  generateResidualSolventsPdfFile,
  readChemUploadPrimaryWorksheetRows,
  readChemUploadSsannWorksheetPayload,
} from "./ChemUploadExcelParseUtils";

function normalizeChemUploadApiResponseResults(response) {
  if (!response || typeof response !== "object") {
    return response;
  }
  let normalizedResults = [];
  if (Array.isArray(response.results)) {
    normalizedResults = response.results;
  } else if (Array.isArray(response.result)) {
    normalizedResults = response.result;
  }
  return { ...response, results: normalizedResults };
}

const emptyGenistaRowMessage = "Failed, make sure template has sample ID of the format \"GB-01282021\"";

/**
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessMetals(file) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildHeavyMetalsUploadList(rows);
  return updateMetalsResults({ list, test_name: "Metals", file_name: file.path || file.name });
}

/**
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessAgilentMetals(file) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildAgilentMetalsUploadList(rows);
  return updateMetalsResults({ list, test_name: "Metals Agilent", file_name: file.path || file.name });
}

/**
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessFap(file) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const genistaId = extractFapGenistaIdFromRows(rows);
  if (!genistaId) {
    return { success: false, message: "Could not find the GB ID", api_name: "fapresults" };
  }
  const response = await sendFapGenistaID(genistaId);
  if (!response?.success) {
    return response;
  }

  const analyticalResults = buildFapAnalyticalResults(rows, FAP_ANALYTE_MAPPING);
  const pdfFileName = `${genistaId}_FAP.pdf`;
  const pdfFile = generateFapPdfFile({
    analyticalResults,
    fileName: pdfFileName,
    fapApiResponse: response,
  });
  const uploadPdfResponse = await uploadFileToS3(
    {
      file: pdfFile,
      key: `media/FAP_results/${pdfFileName}`,
      type: pdfFile.type,
    },
    s3AutomationPrivate,
  );

  if (!uploadPdfResponse?.success) {
    return {
      success: false,
      message: "Failed to upload file to AWS S3",
      api_name: "fapresults",
    };
  }
  const sendPostResponse = await sendParsedValues(genistaId, analyticalResults);
  if (!sendPostResponse?.success) {
    toast.error(sendPostResponse.message);
  }

  return {
    ...response,
    fapPdfPath: uploadPdfResponse.path,
  };
}

/**
 * Residual Solvents -USP upload: extract Genista ID → validate Excel table → API once → PDF → S3.
 * Table parse runs before the API so we do not mark Ready when the workbook is unusable.
 * No panel-result POST / rounding — Excel text is rendered as-is into the PDF.
 *
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessResidualSolvents(file) {
  try {
    if (!file) {
      return {
        success: false,
        message: "No file provided",
        api_name: "residualsolventsresults",
      };
    }

    const rows = await readChemUploadPrimaryWorksheetRows(file);
    const genistaId = extractResidualSolventsGenistaIdFromRows(rows);
    if (!genistaId) {
      return {
        success: false,
        message: "Could not find the GB ID",
        api_name: "residualsolventsresults",
      };
    }

    // Validate table before calling the API (API marks the test Ready on success).
    const table = buildResidualSolventsTableFromRows(rows);
    if (table.error || !table.headers.length || !table.body.length) {
      return {
        success: false,
        message: table.error || "Could not parse Residual Solvents results from the Excel file",
        api_name: "residualsolventsresults",
      };
    }

    const response = await sendResidualSolventsGenistaID(genistaId);
    if (!response?.success) {
      return response;
    }

    const pdfFileName = `${genistaId}_residual_solvents_usp.pdf`;
    let pdfFile;
    try {
      pdfFile = generateResidualSolventsPdfFile({
        headers: table.headers,
        body: table.body,
        fileName: pdfFileName,
        apiResponse: response,
      });
    } catch (pdfError) {
      return {
        success: false,
        message: pdfError?.message || "Failed to generate Residual Solvents PDF",
        api_name: "residualsolventsresults",
      };
    }

    // TEMP (local only): download generated PDF for quick visual check. Remove before prod push.
    // if (
    //   typeof window !== "undefined"
    //   && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    // ) {
    //   const blobUrl = window.URL.createObjectURL(pdfFile);
    //   const link = document.createElement("a");
    //   link.href = blobUrl;
    //   link.download = pdfFileName;
    //   document.body.appendChild(link);
    //   link.click();
    //   link.remove();
    //   window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    // }

    const uploadPdfResponse = await uploadFileToS3(
      {
        file: pdfFile,
        key: `media/Residual_Solvents_results/${pdfFileName}`,
        type: pdfFile.type,
      },
      s3AutomationPrivate,
    );

    if (!uploadPdfResponse?.success) {
      return {
        success: false,
        message: "Failed to upload file to AWS S3",
        api_name: "residualsolventsresults",
      };
    }

    return {
      ...response,
      residualSolventsPdfPath: uploadPdfResponse.path,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || "Residual Solvents upload failed",
      api_name: "residualsolventsresults",
    };
  }
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and full chemistry catalog (dynamic column matching).
 */
export async function chemUploadProcessDynamicOneToOne(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildDynamicOneToOneList({
    rows,
    testList: excelParseContext.testList,
    selectedTestName: excelParseContext.selectedTest?.test,
  });
  const response = await updateDynamicResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for `test_name`.
 */
export async function chemUploadProcessMoistureTemplate(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildMoistureMultiTestList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateMetalsResults({
    list,
    test_name: excelParseContext.selectedTest?.test,
    file_name: file.path || file.name,
  });
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for `test_name`.
 */
export async function chemUploadProcessPolyphenols(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildPolyphenolsResultList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updatePolyphenolResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessSilverNitrate(file) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildSilverNitrateResultList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateSilverNitrateResults(list, "Salt-AgNO3");
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for `test_name`.
 */
export async function chemUploadProcessMycotoxin(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildMycotoxinUploadList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateMycotoxinResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for `test_name`.
 */
export async function chemUploadProcessTitratableAcidity(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildTitratableAcidityUploadList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateTitratableAcitidyResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for `test_name`.
 */
export async function chemUploadProcessPunicalagins(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildPunicalaginsResultList(rows);
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updatePunicalaginResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row (`genistaid_cell`, `result_cell`, `test`) for legacy column rules.
 */
export async function chemUploadProcessLegacyDynamicExcel(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const idCell = Number(excelParseContext.selectedTest?.genistaid_cell);
  const resultCell = Number(excelParseContext.selectedTest?.result_cell);
  const { list, aflatoxinError } = buildLegacyDynamicExcelResultList({
    rows,
    fileName: file.name,
    idCell,
    resultCell,
  });
  if (aflatoxinError) {
    return {
      success: false,
      message: `The value for ${aflatoxinError.errorCellName} in ${aflatoxinError.Genista_id} is invalid. Please ensure that it is either 'n.a.' or a valid result.`,
      results: [],
    };
  }
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and catalog; only `selectedTest` is read for labels.
 */
export async function chemUploadProcessLegacyChemistrySsann(file, excelParseContext) {
  const rows = await readChemUploadSsannWorksheetPayload(file);
  const list = buildChemistryTestResultsList({
    rows,
    fileName: file.name,
    selectedTestName: excelParseContext.selectedTest?.test,
  });
  if (!list.length) {
    return { success: false, message: emptyGenistaRowMessage, results: [] };
  }
  const response = await updateChemistryTestResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

/**
 * @param {File} file Excel workbook from the user.
 * @param {{
 *   selectedTest: object|null,
 *   testList: Array,
 * }} excelParseContext Selected test row and full catalog (dynamic one-to-many row matching).
 */
export async function chemUploadProcessDynamicOneToMany(file, excelParseContext) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  const list = buildDynamicOneToManyList({
    rows,
    testList: excelParseContext.testList,
    selectedTestName: excelParseContext.selectedTest?.test,
  });
  if (!list.length) {
    return {
      success: false,
      message: "No matching Genista rows were found in the selected worksheet.",
      results: [],
    };
  }
  const response = await updateDynamicResults(list, excelParseContext.selectedTest?.test);
  return normalizeChemUploadApiResponseResults(response);
}

const pesticideHistoryProductStub = { xls: [] };

/**
 * @param {File} file Excel workbook from the user.
 * The registry always calls `processFile(file, excelParseContext)`; this handler does not use `excelParseContext`.
 */
export async function chemUploadProcessPesticides(file) {
  const rows = await readChemUploadPrimaryWorksheetRows(file);
  return parsePesticide(rows, file, pesticideHistoryProductStub);
}
